import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Loader2, MapPin, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { CopyBox } from "@/components/pax/PaxPanel";
import { anlopCode } from "@/components/anlop/embedCodes";
import { NewPortDialog } from "@/components/anlop/PortTabs";

const LIMITS = [["1", "Én havn"], ["3", "Inntil 3 havner"], ["custom", "Eget antall"], ["0", "Ubegrenset"]];
const limitKey = (l) => (l === 0 ? "0" : l === 1 ? "1" : l === 3 ? "3" : "custom");

const LimitPicker = ({ row, onChange }) => {
  const [mode, setMode] = useState(limitKey(row.limit));
  const [n, setN] = useState(row.limit > 1 ? row.limit : 5);
  const pick = (m) => { setMode(m); if (m !== "custom") onChange(Number(m)); };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={mode} onChange={(e) => pick(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid={`ports-${row.customer_id}-limit`}>
        {LIMITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {mode === "custom" && (<>
        <Input type="number" min={2} max={100} value={n} onChange={(e) => setN(e.target.value)} className="w-20 h-9" data-testid={`ports-${row.customer_id}-limit-n`} />
        <Button size="sm" variant="outline" disabled={!(Number(n) >= 2)} onClick={() => onChange(Number(n))} data-testid={`ports-${row.customer_id}-limit-save`}>Lagre</Button>
      </>)}
    </div>
  );
};

const PortRow = ({ row, p, onRows }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(p.name);
  const [codes, setCodes] = useState(false);
  const call = async (fn, ok) => {
    try { const { data } = await fn(); onRows(row.customer_id, data); toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li className="py-3 space-y-3" data-testid={`port-${row.customer_id}-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <MapPin className="w-4 h-4 text-sky-700" />
        {edit ? (
          <div className="flex gap-2">
            <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} className="h-8 w-56" data-testid={`port-${p.id}-name-input`} />
            <Button size="sm" disabled={!name.trim()} onClick={() => call(() => api.patch(`/customers/${row.customer_id}/anlop/ports/${p.id}`, { name: name.trim() }), "Navnet er endret").then(() => setEdit(false))} data-testid={`port-${p.id}-name-save`}>Lagre</Button>
          </div>
        ) : <span className="font-medium text-slate-900">{p.name}</span>}
        {p.id === "main" && <span className="text-xs text-slate-500">(første havn)</span>}
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => setCodes((v) => !v)} data-testid={`port-${p.id}-codes`}>Embed-koder<ChevronDown className={`w-4 h-4 ml-1 transition-transform ${codes ? "rotate-180" : ""}`} /></Button>
          <Button size="sm" variant="ghost" onClick={() => setEdit((v) => !v)} aria-label="Gi nytt navn" data-testid={`port-${p.id}-edit`}><Pencil className="w-4 h-4" /></Button>
          {p.id !== "main" && (
            <Button size="sm" variant="ghost" className="text-red-700" aria-label="Slett havn" data-testid={`port-${p.id}-delete`}
              onClick={() => window.confirm(`Slette havnen «${p.name}»? Den kan gjenopprettes her senere.`) && call(() => api.delete(`/customers/${row.customer_id}/anlop/ports/${p.id}`), "Havnen er slettet")}><Trash2 className="w-4 h-4" /></Button>
          )}
        </div>
      </div>
      {codes && (
        <div className="grid lg:grid-cols-2 gap-3 pl-6">
          <CopyBox label="Full anløpsliste" testId={`port-${p.id}-code-list`} text={anlopCode(p.key, "anlop")} />
          <CopyBox label="Neste anløp (teaser)" testId={`port-${p.id}-code-teaser`} text={anlopCode(p.key, "anlop-teaser")} />
        </div>
      )}
    </li>
  );
};

const CustomerPorts = ({ row, onRows }) => {
  const [open, setOpen] = useState(false);
  const setLimit = async (limit) => {
    try { const { data } = await api.patch(`/admin/customers/${row.customer_id}/anlop-ports-limit`, { limit }); onRows(row.customer_id, data); toast.success("Antall havner er oppdatert"); } catch (e) { toast.error(errMsg(e)); }
  };
  const restore = async (pid) => {
    try { const { data } = await api.post(`/admin/customers/${row.customer_id}/anlop/ports/${pid}/restore`); onRows(row.customer_id, data); toast.success("Havnen er gjenopprettet"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5" data-testid={`ports-customer-${row.customer_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-base font-semibold text-slate-900">{row.customer_name}</div>
          <div className="text-xs text-slate-500">{row.ports.length} {row.ports.length === 1 ? "havn" : "havner"}{!row.anlop_enabled && " · Anløpsliste er slått av for kunden"}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LimitPicker key={row.limit} row={row} onChange={setLimit} />
          <Button size="sm" variant="outline" disabled={!row.anlop_enabled || !row.can_add} onClick={() => setOpen(true)} data-testid={`ports-${row.customer_id}-add`}><Plus className="w-4 h-4 mr-1" />Ny havn</Button>
        </div>
      </div>
      <ul className="divide-y divide-slate-100 mt-2">{row.ports.map((p) => <PortRow key={p.id} row={row} p={p} onRows={onRows} />)}</ul>
      {row.region?.enabled && row.ports.length > 1 && (
        <div className="mt-2 pt-3 border-t border-slate-100 space-y-2" data-testid={`ports-${row.customer_id}-region`}>
          <div className="text-sm font-medium text-slate-900">Samleliste for regionen: {row.region.title}</div>
          <div className="grid lg:grid-cols-2 gap-3">
            <CopyBox label="Samleliste (alle havner)" testId={`ports-${row.customer_id}-region-list`} text={anlopCode(row.region.key, "anlop")} />
            <CopyBox label="Neste anløp i regionen (teaser)" testId={`ports-${row.customer_id}-region-teaser`} text={anlopCode(row.region.key, "anlop-teaser")} />
          </div>
        </div>
      )}
      {row.deleted?.length > 0 && (
        <div className="mt-2 pt-3 border-t border-slate-100 space-y-1">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Slettede havner</div>
          {row.deleted.map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-sm text-slate-500" data-testid={`port-deleted-${d.id}`}>
              <span className="line-through">{d.name}</span>
              <Button size="sm" variant="ghost" onClick={() => restore(d.id)} data-testid={`port-${d.id}-restore`}><RotateCcw className="w-4 h-4 mr-1" />Gjenopprett</Button>
            </div>
          ))}
        </div>
      )}
      <NewPortDialog cid={row.customer_id} open={open} onOpenChange={setOpen} onCreated={(d) => onRows(row.customer_id, d)} />
    </div>
  );
};

export const AnlopPortsSection = ({ refreshKey }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/anlop-ports").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const onRows = (cid, d) => setRows((rs) => rs.map((r) => (r.customer_id === cid ? { ...r, ...d } : r)));
  if (!rows) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-700" /></div>;
  return (
    <div className="space-y-3" data-testid="ports-section">
      <p className="text-sm text-slate-500">Velg hvor mange havner hver kunde kan ha i anløpslisten. «Én havn» betyr at kunden ikke ser knappen «Ny havn».</p>
      {rows.map((r) => <CustomerPorts key={r.customer_id} row={r} onRows={onRows} />)}
    </div>
  );
};
